import { Icon, Badge } from '@repo/ui';

interface Transaction {
  id?: string;
  bankTransactionId?: string;
  date?: string;
  amount?: number;
  currency?: string;
  description?: string;
  type?: string;
  matterDisplayNumber?: string;
  matter?: { display_number?: string };
  clientName?: string;
  client?: { name?: string };
  bankAccountName?: string;
  bank_account?: { name?: string };
  source?: string;
  status?: 'new' | 'existing';
}

interface BankTransactionPreviewTableProps {
  transactions: Transaction[];
  title: string;
  variant: 'new' | 'existing' | 'local';
  maxHeight?: string;
}

export function BankTransactionPreviewTable({
  transactions,
  title,
  variant,
  maxHeight = '400px',
}: BankTransactionPreviewTableProps) {
  const formatCurrency = (amount: number | undefined, currency: string | undefined) => {
    if (amount === undefined) return 'N/A';
    return `${currency || '$'}${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getBadgeVariant = () => {
    switch (variant) {
      case 'new':
        return 'success';
      case 'existing':
        return 'warning';
      case 'local':
        return 'default';
      default:
        return 'default';
    }
  };

  const getVariantColor = () => {
    switch (variant) {
      case 'new':
        return 'bg-green-50 dark:bg-green-900/10';
      case 'existing':
        return 'bg-yellow-50 dark:bg-yellow-900/10';
      case 'local':
        return 'bg-xtnd-dark-50 dark:bg-xtnd-dark-700';
      default:
        return 'bg-xtnd-dark-50 dark:bg-xtnd-dark-700';
    }
  };

  if (transactions.length === 0) {
    return (
      <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-6 text-center">
        <Icon name="HiInformationCircle" className="h-12 w-12 text-xtnd-dark-300 mx-auto mb-2" />
        <p className="text-xtnd-dark-400 dark:text-xtnd-dark-300">No {title.toLowerCase()} to display</p>
      </div>
    );
  }

  return (
    <div className={`${getVariantColor()} rounded-lg p-4`}>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">{title}</h4>
        <Badge variant={getBadgeVariant()}>{transactions.length} records</Badge>
      </div>

      <div className="overflow-x-auto" style={{ maxHeight }}>
        <table className="min-w-full divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
          <thead className="bg-xtnd-dark-50 dark:bg-xtnd-dark-800 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                Date
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                Description
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                Type
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                Matter
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                Client
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                Bank Account
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                Source
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-xtnd-dark-700 divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
            {transactions.map((transaction, index) => (
              <tr
                key={transaction.id || transaction.bankTransactionId || index}
                className="hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600 transition-colors"
              >
                <td className="px-3 py-3 text-sm text-xtnd-dark-800 dark:text-white whitespace-nowrap">
                  {transaction.date || 'N/A'}
                </td>
                <td className="px-3 py-3 text-sm text-xtnd-dark-800 dark:text-white whitespace-nowrap font-medium">
                  {formatCurrency(transaction.amount, transaction.currency)}
                </td>
                <td className="px-3 py-3 text-sm text-xtnd-dark-800 dark:text-white max-w-xs truncate">
                  {transaction.description || 'N/A'}
                </td>
                <td className="px-3 py-3 text-sm text-xtnd-dark-800 dark:text-white whitespace-nowrap">
                  {transaction.type || 'N/A'}
                </td>
                <td className="px-3 py-3 text-sm text-xtnd-dark-800 dark:text-white whitespace-nowrap">
                  {transaction.matterDisplayNumber ||
                    transaction.matter?.display_number ||
                    'N/A'}
                </td>
                <td className="px-3 py-3 text-sm text-xtnd-dark-800 dark:text-white whitespace-nowrap">
                  {transaction.clientName || transaction.client?.name || 'N/A'}
                </td>
                <td className="px-3 py-3 text-sm text-xtnd-dark-800 dark:text-white whitespace-nowrap">
                  {transaction.bankAccountName || transaction.bank_account?.name || 'N/A'}
                </td>
                <td className="px-3 py-3 text-sm text-xtnd-dark-800 dark:text-white whitespace-nowrap">
                  {transaction.source || 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
